import React, { useState, useEffect, useCallback, useRef } from "react";
import { getRegion } from "../../utils/region";
import { SiTwitch, SiYoutube, SiKick, SiRumble } from "react-icons/si";
import { axiosInstance } from "../../config";
import toast from "react-hot-toast";
import "./LiveClippingModal.css";

// Real brand logos (official brandmarks), tinted via the tile's currentColor.
const BRAND_ICONS = { twitch: SiTwitch, youtube: SiYoutube, kick: SiKick, rumble: SiRumble };
function BrandMark({ id }) {
  const Icon = BRAND_ICONS[id];
  return Icon ? <Icon size={24} /> : (id ? id.charAt(0).toUpperCase() : "");
}

// Brand hex → rgba string, so each tile can glow in its own colour.
function hexToRgba(hex, a) {
  const h = String(hex || "").replace("#", "");
  const full = h.length === 3 ? h.split("").map((c) => c + c).join("") : h;
  const n = parseInt(full, 16);
  return `rgba(${(n >> 16) & 255}, ${(n >> 8) & 255}, ${n & 255}, ${a})`;
}

const POLL_MS = 15000;
const LIVE_PLANS = ["Pro", "Premium"];
// Hosts where a "live" URL might actually be a finished broadcast: YouTube keeps
// an ended stream at the same youtube.com/live/<id> address. Twitch/Kick channel
// URLs are live-or-nothing, so they can be paywalled without a probe.
const AMBIGUOUS_LIVE_URL = /(youtube\.com|youtu\.be)\//i;

// Platforms you can connect for continuous live auto-clipping. `soon` platforms
// are shown but disabled until the Modal worker implements detection + HLS pull
// for them (see LIVE_CHANNEL_RESEARCH.md) — flip `soon` off to enable.
const PLATFORMS = [
  { id: "twitch", name: "Twitch", color: "#9146FF", prefix: "twitch.tv/", ph: "yourname", oauth: true },
  { id: "youtube", name: "YouTube", color: "#FF0000", prefix: "youtube.com/@", ph: "yourhandle", oauth: true },
  { id: "kick", name: "Kick", color: "#53FC18", prefix: "kick.com/", ph: "yourname", oauth: true },
  { id: "rumble", name: "Rumble", color: "#85C742", prefix: "rumble.com/c/", ph: "YourChannel", soon: true },
];

// Build a channel URL from the picked platform + handle. If the user pasted a
// full URL, use it as-is.
function buildChannelUrl(platform, handle) {
  const h = (handle || "").trim();
  if (!h) return "";
  if (/^https?:\/\//i.test(h)) return h;
  const u = h.replace(/^@/, "");
  switch (platform) {
    case "youtube": return `https://youtube.com/@${u}/live`;
    case "kick": return `https://kick.com/${u}`;
    case "rumble": return `https://rumble.com/c/${u}`;
    default: return `https://twitch.tv/${u}`;
  }
}

function fmtClock(sec) {
  if (sec == null || !Number.isFinite(sec)) return "";
  const s = Math.max(0, Math.floor(sec));
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const ss = s % 60;
  const pad = (n) => String(n).padStart(2, "0");
  return h > 0 ? `${h}:${pad(m)}:${pad(ss)}` : `${m}:${pad(ss)}`;
}

function StatusBadge({ status }) {
  if (status === "active" || status === "starting") {
    return <span className="lcm-badge lcm-badge--live"><span className="lcm-dot" />LIVE</span>;
  }
  if (status === "ended") return <span className="lcm-badge lcm-badge--ended">ENDED</span>;
  if (status === "error") return <span className="lcm-badge lcm-badge--error">ERROR</span>;
  return <span className="lcm-badge">{status}</span>;
}

function LiveClip({ clip }) {
  // Download the BURNED (captioned) clip, not the clean version.
  const dl = clip.url || clip.cleanUrl;
  return (
    <div className="lcm-clip">
      <div className="lcm-clip-vid-wrap">
        {clip.url ? (
          <video className="lcm-clip-vid" src={clip.url} poster={clip.thumbnailUrl || undefined}
                 controls preload="none" playsInline />
        ) : (
          <div className="lcm-clip-pending">rendering…</div>
        )}
        {Number.isFinite(clip.broadcastStartSec) && (
          <span className="lcm-clip-ts">@ {fmtClock(clip.broadcastStartSec)}</span>
        )}
        {Number.isFinite(clip.duration) && (
          <span className="lcm-clip-dur">{Math.round(clip.duration)}s</span>
        )}
      </div>
      <div className="lcm-clip-body">
        <p className="lcm-clip-hook" title={clip.hook}>{clip.hook || "Untitled clip"}</p>
        {dl && <a className="lcm-clip-dl" href={dl} target="_blank" rel="noreferrer" download>Download</a>}
      </div>
    </div>
  );
}

/**
 * LiveClippingModal — entry point for continuous Twitch live clipping from the
 * home page. Three phases:
 *   gate    → user isn't on Pro/Premium; show upgrade CTA.
 *   start   → fresh: pick cadence, confirm the live URL, start.
 *   session → a session is running; poll + stream clips in, allow stop.
 *
 * Props: { open, onClose, url, userPlan, sessionId, onUpgrade, onNotLive }
 *   url       — prefilled Twitch link (from the paste on home)
 *   sessionId — open straight to a running session (from the "live active" banner)
 *   onUpgrade — called when a non-Pro user clicks upgrade
 *   onNotLive — called with the URL when the worker reports the stream isn't
 *               live. The parent decides what to do (a YouTube /live/ link is
 *               still a valid VOD once the broadcast ends, so home falls back
 *               to normal clip generation instead of erroring).
 */
// "Style" tab — the per-channel auto-clip style applied to every live auto-clip.
// Captions inherit the user's saved caption style (set in Me); this controls
// aspect, clip count, and which burn-ins are on.
function StyleTab() {
  const [loaded, setLoaded] = useState(false);
  const [saving, setSaving] = useState(false);
  const [s, setS] = useState({
    aspectRatio: "9:16", maxClips: "auto",
    burnHookTitle: true, burnSubtitles: true, burnWatermark: true,
  });

  useEffect(() => {
    axiosInstance.get("/users/me/creator-settings")
      .then((r) => {
        const ac = r.data?.creatorSettings?.autoClip || {};
        setS({
          aspectRatio: ac.aspectRatio === "16:9" ? "16:9" : "9:16",
          maxClips: ac.maxClips || "auto",
          burnHookTitle: ac.burnHookTitle !== false,
          burnSubtitles: ac.burnSubtitles !== false,
          burnWatermark: ac.burnWatermark !== false,
        });
      })
      .catch(() => {})
      .finally(() => setLoaded(true));
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      await axiosInstance.put("/users/me/auto-clip-settings", s);
      toast.success("Auto-clip style saved");
    } catch { toast.error("Couldn't save. Try again."); }
    finally { setSaving(false); }
  };

  const toggle = (k) => setS((p) => ({ ...p, [k]: !p[k] }));
  // Title captions + subtitles aren't a feature on 16:9 (wide) — watermark only.
  const titleSubsOk = s.aspectRatio !== "16:9";
  const CHIPS = [
    ...(titleSubsOk ? [
      { k: "burnHookTitle", label: "Title captions" },
      { k: "burnSubtitles", label: "Subtitles" },
    ] : []),
    { k: "burnWatermark", label: "Channel watermark" },
  ];

  if (!loaded) return <div className="lcm2-style-loading">Loading…</div>;

  return (
    <div className="lcm2-style">
      <div className="lcm2-style-group">
        <span className="lcm2-style-label">Aspect ratio</span>
        <div className="lcm2-style-aspect">
          {[["9:16", "Vertical"], ["16:9", "Wide"]].map(([r, sub]) => (
            <button key={r} type="button"
              className={`lcm2-style-ar${s.aspectRatio === r ? " on" : ""}`}
              onClick={() => setS((p) => ({ ...p, aspectRatio: r }))}>
              <b>{r}</b><small>{sub}</small>
            </button>
          ))}
        </div>
      </div>

      <div className="lcm2-style-group">
        <span className="lcm2-style-label">Overlays</span>
        <div className="lcm2-style-chips">
          {CHIPS.map((c) => (
            <button key={c.k} type="button"
              className={`lcm2-style-chip${s[c.k] ? " on" : ""}`}
              onClick={() => toggle(c.k)}>
              <span className="lcm2-style-chip-ic">{s[c.k] ? "✓" : "+"}</span>{c.label}
            </button>
          ))}
        </div>
      </div>
      <button className="lcm2-style-save" onClick={save} disabled={saving}>
        {saving ? "Saving…" : "Save auto-clip style"}
      </button>
    </div>
  );
}

export default function LiveClippingModal({
  open, onClose, url = "", userPlan, sessionId = null,
  aspectRatio = "9:16", burnHookTitle = true, burnSubtitles = true, burnWatermark = false,
  twitchConn = null, onTwitchDisconnect,
  kickConn = null, onKickDisconnect,
  youtubeConn = null, onYoutubeDisconnect,
  onUpgrade, onNotLive,
}) {
  // Pro price for the unlock button, regional discount included.
  const [proPriceLabel, setProPriceLabel] = useState("$29");
  useEffect(() => {
    getRegion().then((r) => {
      if (r.discountPct > 0) {
        const p = Math.round(29 * (100 - r.discountPct)) / 100;
        setProPriceLabel(`$${Number.isInteger(p) ? p : p.toFixed(2)}`);
      }
    });
  }, []);
  const isPro = LIVE_PLANS.includes(userPlan);
  const [disconnecting, setDisconnecting] = useState(null); // platform id mid-disconnect

  // Connected accounts keyed by platform, so each channel row can render its
  // connected state + a Disconnect action.
  const CONNS = { twitch: twitchConn, kick: kickConn, youtube: youtubeConn };
  const DISCONNECT_CB = { twitch: onTwitchDisconnect, kick: onKickDisconnect, youtube: onYoutubeDisconnect };
  // Only ONE streaming platform per brand — the others lock until you disconnect.
  const connectedPlatform = Object.keys(CONNS).find((k) => CONNS[k]?.connected) || null;

  const handleDisconnect = async (platformId) => {
    if (disconnecting) return;
    setDisconnecting(platformId);
    try {
      await axiosInstance.post(`/connect/${platformId}/disconnect`);
      toast.success(`${PLATFORMS.find((p) => p.id === platformId)?.name || "Channel"} disconnected`);
      DISCONNECT_CB[platformId]?.();
    } catch {
      toast.error("Couldn't disconnect. Try again.");
    } finally {
      setDisconnecting(null);
    }
  };

  const [phase, setPhase] = useState("start");
  // Platform picker + channel handle. Cadence is fixed at 5 min server-side —
  // users don't choose an interval anymore. platform starts unset so the popup
  // opens as a clean stacked list of channels.
  const [platform, setPlatform] = useState(null);
  const [channel, setChannel] = useState("");
  const [connecting, setConnecting] = useState(null); // platform id mid-OAuth-redirect
  const [starting, setStarting] = useState(false);
  const [stopping, setStopping] = useState(false);
  const [session, setSession] = useState(null);
  const [activeId, setActiveId] = useState(sessionId);
  const [tab, setTab] = useState("channels"); // "channels" | "style"
  const pollRef = useRef(null);

  const fetchSession = useCallback(async (id) => {
    if (!id) return;
    try {
      const res = await axiosInstance.get(`/live/${id}`);
      if (res.data?.session) setSession(res.data.session);
    } catch { /* transient */ }
  }, []);

  // Poll while the session is live.
  useEffect(() => {
    if (pollRef.current) clearInterval(pollRef.current);
    const live = open && phase === "session" && session &&
      (session.status === "active" || session.status === "starting");
    if (live && activeId) {
      pollRef.current = setInterval(() => fetchSession(activeId), POLL_MS);
    }
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
  }, [open, phase, session, activeId, fetchSession]);

  const handleStart = useCallback(async () => {
    const link = url ? url.trim() : buildChannelUrl(platform, channel);
    if (!link) { toast.error("Enter your channel name first"); return; }
    setStarting(true);
    try {
      const res = await axiosInstance.post("/live/start", {
        videoUrl: link,
        aspectRatio,
        burnHookTitle,
        burnSubtitles,
        burnWatermark,
      });
      toast.success("You're live! Clips roll in as moments happen.");
      const id = res.data?.liveSessionId;
      setActiveId(id);
      setSession(res.data?.session || null);
      setPhase("session");
      if (id) fetchSession(id);
    } catch (err) {
      const code = err.response?.data?.error;
      if (code === "pro_required") {
        setPhase("gate");
      } else if (code === "not_live") {
        // Broadcast already ended (or never started). Hand it back to the
        // parent — on home that means clipping the replay as a normal VOD.
        onNotLive?.(link);
      } else if (code === "live_already_running") {
        // One session at a time — jump to the one that's already running.
        const id = err.response?.data?.liveSessionId;
        toast.error("You already have a live session running.");
        if (id) { setActiveId(id); setPhase("session"); fetchSession(id); }
      } else if (code === "out_of_credits") {
        toast.error("You're out of credits.");
        onUpgrade?.();
      } else {
        toast.error(err.response?.data?.message || err.response?.data?.error || "Couldn't start live clipping");
      }
    } finally {
      setStarting(false);
    }
  }, [url, platform, channel, aspectRatio, burnHookTitle, burnSubtitles,
      burnWatermark, fetchSession, onNotLive, onUpgrade]);

  // Stable handle for the init effect (see its dep-array note).
  const startRef = useRef(handleStart);
  useEffect(() => { startRef.current = handleStart; }, [handleStart]);

  // (Re)initialize when the modal opens.
  useEffect(() => {
    if (!open) return;
    if (!isPro) {
      // A Twitch/Kick channel URL is unambiguously a live-clipping request, so
      // paywall it right away. A YouTube `/live/<id>` URL is NOT: it serves the
      // replay once the broadcast ends, and clipping a replay is included in
      // every paid plan. Ask the server (it probes, then answers pro_required
      // or not_live) instead of showing an upgrade wall the user may not need.
      if (!sessionId && AMBIGUOUS_LIVE_URL.test(url || "")) {
        setPhase("checking");
        startRef.current?.();
      } else {
        setPhase("gate");
      }
      return;
    }
    if (sessionId) {
      setActiveId(sessionId);
      setPhase("session");
      fetchSession(sessionId);
    } else {
      setActiveId(null);
      setSession(null);
      setPhase("start");
      setPlatform(null);
      setChannel("");
    }
    // handleStart is deliberately NOT a dep — it closes over parent callbacks
    // that change identity on every parent render, which would re-run this
    // effect (and reset a running session) constantly. Called via startRef.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, isPro, sessionId, url, fetchSession]);

  if (!open) return null;

  const handleStop = async () => {
    if (!activeId) return;
    setStopping(true);
    try {
      await axiosInstance.post(`/live/${activeId}/stop`);
      toast.success("Live clipping stopped");
      fetchSession(activeId);
    } catch {
      toast.error("Couldn't stop the session");
    } finally {
      setStopping(false);
    }
  };

  const clips = (session?.clips || []).slice().sort(
    (a, b) => (b.broadcastStartSec || 0) - (a.broadcastStartSec || 0)
  );
  const isLive = session && (session.status === "active" || session.status === "starting");

  return (
    <div className="lcm-overlay" onClick={onClose}>
      <div className="lcm-panel" onClick={(e) => e.stopPropagation()}>
        <button className="lcm-close" onClick={onClose} aria-label="Close">×</button>

        {/* ── GATE: non-Pro ── */}
        {/* ── CHECKING: is this link actually live? (see init effect) ── */}
        {phase === "checking" && (
          <div className="lcm-gate">
            <div className="lcm-gate-icon">⏳</div>
            <h2 className="lcm-gate-title">Checking this stream…</h2>
            <p className="lcm-gate-sub">
              One sec — working out whether this one's live right now or an
              already-finished stream we can just clip.
            </p>
          </div>
        )}

        {/* Eklipse-style contextual unlock: what they get at the moment they want it. */}
        {phase === "gate" && (
          <div className="lcm-gate lcm-gate--unlock">
            <div className="lcm-lock" aria-hidden="true">
              <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><rect x="4" y="11" width="16" height="10" rx="2.5" /><path d="M8 11V7.5a4 4 0 0 1 8 0V11" /></svg>
            </div>
            <h2 className="lcm-gate-title">Unlock live clipping</h2>
            <p className="lcm-gate-sub">Clipbait clips your stream <strong>while you're live</strong>. Clips land every few minutes, ready to post.</p>
            <div className="lcm-compare">
              <div className="lcm-tier lcm-tier--free">
                <span className="lcm-tier-name">Your plan</span>
                <span className="lcm-tier-val">Off</span>
              </div>
              <span className="lcm-tier-arrow" aria-hidden="true">→</span>
              <div className="lcm-tier lcm-tier--pro">
                <span className="lcm-tier-name">⚡ Pro</span>
                <span className="lcm-tier-val">Included</span>
              </div>
            </div>
            <button className="lcm-primary lcm-primary--unlock" onClick={() => { onClose?.(); onUpgrade?.(); }}>
              Unlock live clipping · {proPriceLabel}/mo
            </button>
            <button className="lcm-notnow" onClick={() => onClose?.()}>Not now</button>
          </div>
        )}

        {/* ── START: Pro, fresh session — clean stacked channel options ── */}
        {phase === "start" && (
          <div className="lcm-start">
            {url ? (
              <>
                <p className="lcm-url" title={url}>{url}</p>
                <button className="lcm-primary" onClick={handleStart} disabled={starting}>
                  {starting ? "Connecting…" : "Start auto-clipping"}
                </button>
              </>
            ) : !platform ? (
              /* Connect-your-channel grid */
              <>
                <div className="lcm2-head">
                  <h2 className="lcm2-title">Connect your channel</h2>
                  <p className="lcm2-sub">Auto-clips your best moments in real time, live while you stream.</p>
                </div>
                <div className="lcm2-tabs">
                  <button type="button" className={`lcm2-tab${tab === "channels" ? " on" : ""}`} onClick={() => setTab("channels")}>Channels</button>
                  <button type="button" className={`lcm2-tab${tab === "style" ? " on" : ""}`} onClick={() => setTab("style")}>Auto-clip style</button>
                </div>
                {tab === "style" ? <StyleTab /> : (
                <div className="lcm2-grid">
                  {PLATFORMS.map((p) => {
                    const conn = CONNS[p.id];
                    const connected = !!conn?.connected;
                    const locked = connectedPlatform && connectedPlatform !== p.id && !p.soon && !connected;
                    const dark = p.id === "kick" || p.id === "rumble";
                    const stateCls = p.soon ? "soon" : connected ? "on" : locked ? "locked" : "";
                    const brandVars = connected ? {
                      "--b-bg": hexToRgba(p.color, 0.09),
                      "--b-bd": hexToRgba(p.color, 0.55),
                      "--b-glow": `0 16px 46px -16px ${hexToRgba(p.color, 0.6)}`,
                    } : undefined;
                    return (
                      <button
                        key={p.id}
                        type="button"
                        className={`lcm2-tile ${stateCls}`}
                        style={brandVars}
                        disabled={p.soon || (!!connecting && !connected)}
                        onClick={() => {
                          if (p.soon) return;
                          if (connected) { handleDisconnect(p.id); return; }
                          if (locked) { toast(`Disconnect ${PLATFORMS.find((x) => x.id === connectedPlatform)?.name || "your channel"} first to switch.`); return; }
                          if (connecting) return;
                          if (p.oauth) { setConnecting(p.id); window.location.href = `/api/connect/${p.id}`; return; }
                          setPlatform(p.id); setChannel("");
                        }}
                      >
                        <span className="lcm2-brand" style={{ background: p.color, color: dark ? "#0a0a0b" : "#fff" }}>
                          <BrandMark id={p.id} />
                        </span>
                        <div className="lcm2-tile-meta">
                          <span className="lcm2-name">{p.name}</span>
                          {p.soon ? (
                            <span className="lcm2-status">Coming soon</span>
                          ) : connected ? (
                            <span className="lcm2-disc-btn">
                              {disconnecting === p.id ? <span className="lcm2-btn-spin" /> : "Disconnect"}
                            </span>
                          ) : locked ? (
                            <span className="lcm2-status">Locked</span>
                          ) : (
                            <span className="lcm2-connect-btn">
                              {connecting === p.id ? <span className="lcm2-btn-spin" /> : "Connect"}
                            </span>
                          )}
                        </div>
                        {p.soon ? (
                          <span className="lcm2-soon">SOON</span>
                        ) : connected ? (
                          <span className="lcm2-badge" style={{ background: p.color, color: dark ? "#0a0a0b" : "#fff" }}>
                            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3.2" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12" /></svg>
                          </span>
                        ) : null}
                      </button>
                    );
                  })}
                </div>
                )}
              </>
            ) : (
              /* Selected a platform — enter the handle */
              <div className="lcm-connect">
                <button className="lcm-back" onClick={() => setPlatform(null)}>
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><polyline points="15 18 9 12 15 6" /></svg>
                  {PLATFORMS.find((p) => p.id === platform)?.name}
                </button>
                <div className="lcm-channel">
                  <span className="lcm-channel-prefix">{PLATFORMS.find((p) => p.id === platform)?.prefix}</span>
                  <input
                    className="lcm-channel-input"
                    type="text"
                    value={channel}
                    onChange={(e) => setChannel(e.target.value)}
                    onKeyDown={(e) => { if (e.key === "Enter" && !starting) handleStart(); }}
                    placeholder={PLATFORMS.find((p) => p.id === platform)?.ph}
                    autoFocus
                  />
                </div>
                <button className="lcm-primary" onClick={handleStart} disabled={starting}>
                  {starting ? "Connecting…" : "Start auto-clipping"}
                </button>
              </div>
            )}
          </div>
        )}

        {/* ── SESSION: running ── */}
        {phase === "session" && (
          <div className="lcm-session">
            <div className="lcm-session-head">
              <div className="lcm-session-meta">
                <div className="lcm-session-titleline">
                  <StatusBadge status={session?.status} />
                  <span className="lcm-session-name">
                    {session?.videoTitle || session?.videoAuthor || session?.channelUrl || "Live session"}
                  </span>
                </div>
                <div className="lcm-session-stats">
                  {clips.length} clip{clips.length === 1 ? "" : "s"} · {session?.windowCount || 0} checks
                </div>
              </div>
              {isLive && (
                <button className="lcm-stop" onClick={handleStop} disabled={stopping}>
                  {stopping ? "Stopping…" : "Stop"}
                </button>
              )}
            </div>

            {session?.status === "error" && (
              <p className="lcm-error">{session.error || "This session failed to start."}</p>
            )}

            {clips.length === 0 ? (
              <div className="lcm-empty">
                {isLive
                  ? "Watching the stream… clips appear here as moments happen."
                  : "No clips were captured this session."}
              </div>
            ) : (
              <div className="lcm-grid">
                {clips.map((c) => <LiveClip key={c._id || c.broadcastStartSec} clip={c} />)}
              </div>
            )}

            {isLive && (
              <p className="lcm-bg-note">You can close this window, clipping keeps running.</p>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
